import { Card, Row, Col, Statistic, Space, Typography, Tag, Spin } from "antd";
import { useApiUrl } from "@refinedev/core";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  AreaChartOutlined,
} from "@ant-design/icons";
import type { ControlPointStatistics } from "../../types/control-point";

const { Title, Text } = Typography;

/**
 * ControlPointStats Component
 * Displays statistics widget for control points on the dashboard
 * Shows totals, status distribution, and sample results
 */
export const ControlPointStats = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const [stats, setStats] = useState<ControlPointStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        const token = localStorage.getItem("access_token");
        const response = await axios.get<ControlPointStatistics>(
          `${apiUrl}/control-points/statistics/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching control point statistics:", error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatistics();
  }, [apiUrl]);

  if (isLoading) {
    return (
      <Card title={t("controlPoint.statistics.title", "Puntos de Control")}>
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (isError || !stats) {
    return (
      <Card title={t("controlPoint.statistics.title", "Puntos de Control")}>
        <Text type="danger">
          {t("controlPoint.statistics.error", "Error al cargar estadísticas")}
        </Text>
      </Card>
    );
  }

  const handleViewAll = () => {
    navigate("/control-points");
  };

  // Calculate percentages for visual representation
  const totalCPs = stats?.total_control_points || 0;
  const openPercentage = totalCPs > 0 ? ((stats?.by_status.open || 0) / totalCPs) * 100 : 0;
  const closedPercentage = totalCPs > 0 ? ((stats?.by_status.closed || 0) / totalCPs) * 100 : 0;

  // Sample results percentages
  const totalWithSamples =
    (stats?.by_samples_result.positive || 0) +
    (stats?.by_samples_result.negative || 0) +
    (stats?.by_samples_result.pending || 0);
  const positivePercentage =
    totalWithSamples > 0 ? ((stats?.by_samples_result.positive || 0) / totalWithSamples) * 100 : 0;
  const negativePercentage =
    totalWithSamples > 0 ? ((stats?.by_samples_result.negative || 0) / totalWithSamples) * 100 : 0;
  const pendingPercentage =
    totalWithSamples > 0 ? ((stats?.by_samples_result.pending || 0) / totalWithSamples) * 100 : 0;

  return (
    <Card
      title={
        <Space>
          <AreaChartOutlined />
          <span>{t("controlPoint.statistics.title", "Puntos de Control")}</span>
        </Space>
      }
      extra={
        <a onClick={handleViewAll} style={{ cursor: "pointer" }}>
          {t("controlPoint.statistics.viewAll", "Ver todos")}
        </a>
      }
      style={{ height: "100%" }}
    >
      {/* Main Statistics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Statistic
            title={t("controlPoint.statistics.totalPoints", "Total Puntos")}
            value={stats.total_control_points}
            prefix={<AreaChartOutlined />}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title={t("controlPoint.statistics.totalSamples", "Total Muestras")}
            value={stats.total_samples}
            prefix={<ExperimentOutlined />}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title={t("controlPoint.statistics.convertedHectares", "Hectáreas Convertidas")}
            value={stats.total_converted_hectares}
            precision={2}
            suffix="ha"
          />
        </Col>
      </Row>

      {/* Status Distribution */}
      <div style={{ marginTop: 24 }}>
        <Title level={5}>
          {t("controlPoint.statistics.statusDistribution", "Distribución por Estado")}
        </Title>
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tag color="green" icon={<CheckCircleOutlined />}>
              {t("controlPoint.status.open", "Abiertos")}
            </Tag>
            <Text strong>{stats.by_status.open}</Text>
            <Text type="secondary">({openPercentage.toFixed(1)}%)</Text>
            <div
              style={{
                flex: 1,
                height: 8,
                backgroundColor: "#f0f0f0",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${openPercentage}%`,
                  height: "100%",
                  backgroundColor: "#52c41a",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tag color="red" icon={<CloseCircleOutlined />}>
              {t("controlPoint.status.closed", "Cerrados")}
            </Tag>
            <Text strong>{stats.by_status.closed}</Text>
            <Text type="secondary">({closedPercentage.toFixed(1)}%)</Text>
            <div
              style={{
                flex: 1,
                height: 8,
                backgroundColor: "#f0f0f0",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${closedPercentage}%`,
                  height: "100%",
                  backgroundColor: "#ff4d4f",
                }}
              />
            </div>
          </div>
        </Space>
      </div>

      {/* Crop Status Distribution */}
      <div style={{ marginTop: 24 }}>
        <Title level={5}>
          {t("controlPoint.statistics.cropStatusDistribution", "Estado de Cultivos")}
        </Title>
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("controlPoint.cropStatus.active", "Activos")}
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {stats.by_crop_status.active}
              </Text>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("controlPoint.cropStatus.removed", "Eliminados")}
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {stats.by_crop_status.removed}
              </Text>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("controlPoint.cropStatus.authorized", "Autorizados")}
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {stats.by_crop_status.authorized}
              </Text>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("controlPoint.cropStatus.pendingSamples", "Pendiente Muestras")}
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {stats.by_crop_status.pending_samples}
              </Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Sample Results Distribution */}
      {totalWithSamples > 0 && (
        <div style={{ marginTop: 24 }}>
          <Title level={5}>
            {t(
              "controlPoint.statistics.sampleResultsDistribution",
              "Resultados de Muestras"
            )}
          </Title>
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Tag color="red">{t("controlPoint.samplesResult.positive", "Positivas")}</Tag>
              <Text strong>{stats.by_samples_result.positive}</Text>
              <Text type="secondary">({positivePercentage.toFixed(1)}%)</Text>
              <div
                style={{
                  flex: 1,
                  height: 8,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${positivePercentage}%`,
                    height: "100%",
                    backgroundColor: "#ff4d4f",
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Tag color="green">{t("controlPoint.samplesResult.negative", "Negativas")}</Tag>
              <Text strong>{stats.by_samples_result.negative}</Text>
              <Text type="secondary">({negativePercentage.toFixed(1)}%)</Text>
              <div
                style={{
                  flex: 1,
                  height: 8,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${negativePercentage}%`,
                    height: "100%",
                    backgroundColor: "#52c41a",
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Tag color="gold">{t("controlPoint.samplesResult.pending", "Pendientes")}</Tag>
              <Text strong>{stats.by_samples_result.pending}</Text>
              <Text type="secondary">({pendingPercentage.toFixed(1)}%)</Text>
              <div
                style={{
                  flex: 1,
                  height: 8,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${pendingPercentage}%`,
                    height: "100%",
                    backgroundColor: "#faad14",
                  }}
                />
              </div>
            </div>
          </Space>
        </div>
      )}

      {/* Average Conversion */}
      {stats.average_hectares_per_conversion > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "controlPoint.statistics.averageConversion",
              "Promedio de hectáreas por conversión"
            )}
          </Text>
          <div>
            <Text strong style={{ fontSize: 18 }}>
              {stats.average_hectares_per_conversion.toFixed(2)} ha
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};
